import React from 'react';
import { styled, css } from 'styled-components';
import { defaultTheme } from '@hn-ui/themes';
import { Tooltip } from '@hn-ui/tooltips';

export const testType = (type) => {
  switch (type) {
    case 'middle':
      return { short: 'MT', long: 'Mid-year test' };
    case 'finally':
      return { short: 'ET', long: 'End-of-year test' };
    case 'assignment':
      return { short: 'A', long: 'Assignment' };
    case 'primary':
    default:
      return { short: 'PT', long: 'Placement test' };
  }
};

export const getColorByStatus = (status) => {
  switch (status) {
    case 'completed':
      return defaultTheme.colors.green50;
    case 'expired':
    case 'canceled':
      return defaultTheme.colors.red50;
    case 'in_progress':
    default:
      return defaultTheme.colors.black50;
  }
};

const Container = styled.div`
  width: 24px;
  height: 24px;

  margin-top: 0;
  margin-bottom: 0;
  margin-right: 6px;

  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  font-family: var(--main-font);
  font-style: normal;
  letter-spacing: 0.6px;
  white-space: pre-line;
  text-align: center;
  text-transform: none;
  color: ${({ theme }) => theme.colors.black10};

  border-radius: 2px;

  background: ${({ isActive, theme }) =>
    isActive ? theme.colors.purple80 : '#c5c7d4'};

  ${({ status }) =>
    status &&
    css`
      font-size: 13px;
      letter-spacing: -0.2px;
    `}
`;

const TooltipHeader = styled.header`
  min-width: 265px;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
`;

const AssignmentTooltipHeader = styled(TooltipHeader)`
  margin-bottom: 8px;
`;

const TooltipHeading = styled.h1`
  display: flex;

  margin: 0;

  font-family: var(--main-font);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: #242527;
`;

const TooltipStatus = styled.p`
  margin-bottom: 0;

  font-family: var(--main-font);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-align: right;

  color: ${({ status }) => getColorByStatus(status)};
`;

const TooltipBody = styled.div`
  max-height: 80vh;
  overflow: auto;
`;

const TooltipDescriptionContainer = styled.div`
  font-family: var(--main-font);
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  color: #242527;

  align-self: stretch;

  p:not(:last-child) {
    margin-bottom: 4px;
  }
`;

const AssignmentTooltipDescriptionContainer = styled(
  TooltipDescriptionContainer
)`
  margin-bottom: 8px;
`;

const TooltipDescriptionPart = styled.p`
  margin: 0;
`;

const TooltipFooter = styled.footer`
  font-feature-settings: 'clig' off, 'liga' off;
  font-family: var(--main-font);
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.1px;
  text-align: left;
  color: ${({ theme }) => theme.colors.black10};

  p:not(:last-child) {
    margin-bottom: 8px;
  }
`;

export const Day = ({ isModalOpen, type, status, content }) => {
  const isActive = !(status === 'canceled' || status === 'expired');

  return (
    <Tooltip maxWidth={450} interactive={!isModalOpen} content={content}>
      <Container isActive={isActive}>{testType(type).short}</Container>
    </Tooltip>
  );
};

const parseState = (status) =>
  ['canceled_by_teacher', 'canceled_by_time', 'canceled_by_card'].includes(
    status
  )
    ? 'canceled'
    : status.replace('_', ' ');

export const AssignmentDay = ({
  type,
  practiceTime,
  assignments,
  isModalOpen,
}) => {
  const isActive = !assignments.every(
    ({ state }) => state === 'canceled' || state === 'expired'
  );

  return (
    <Tooltip
      maxWidth={450}
      interactive={!isModalOpen}
      content={{
        body: (
          <TooltipBody>
            {assignments.map(
              ({
                id,
                name,
                time_spent: timeSpent,
                accuracy,
                state: status,
              }) => (
                <React.Fragment key={id}>
                  <AssignmentTooltipHeader>
                    <TooltipHeading>{name}</TooltipHeading>
                    <TooltipStatus status={status}>
                      {parseState(status)}
                    </TooltipStatus>
                  </AssignmentTooltipHeader>

                  <AssignmentTooltipDescriptionContainer>
                    <TooltipDescriptionPart>
                      {`Time spent: ${timeSpent} min`}
                    </TooltipDescriptionPart>
                    <TooltipDescriptionPart>
                      {`Accuracy: ${accuracy} %`}
                    </TooltipDescriptionPart>
                  </AssignmentTooltipDescriptionContainer>
                </React.Fragment>
              )
            )}
          </TooltipBody>
        ),
        caption: practiceTime > 0 && (
          <TooltipFooter>{`+ practice time: ${practiceTime} min`}</TooltipFooter>
        ),
      }}
    >
      <Container isActive={isActive}>{testType(type).short}</Container>
    </Tooltip>
  );
};
